import React, { useState, useRef, useEffect } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CameraIcon from "@mui/icons-material/Camera";

const SafetyImageCommentModal = ({ open, onClose,setSelectedQuestion, onSubmit, loader }) => {
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]); // Multiple files
  const [previewUrls, setPreviewUrls] = useState([]); // Multiple previews
  const [cameraMode, setCameraMode] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Start or stop camera when mode changes
  useEffect(() => {
    if (cameraMode) startCamera();
    else stopCamera();
    return () => stopCamera();
  }, [cameraMode]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      console.error("Camera access denied:", err);
      alert("Unable to access camera. Please allow camera permission.");
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const captureImage = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (canvas && video) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/png");
      setPreviewUrls((prev) => [...prev, dataUrl]);
      setFiles((prev) => [...prev, dataUrl]);
      setCameraMode(false);
      stopCamera();
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      const newUrls = selectedFiles.map((f) => URL.createObjectURL(f));
      setFiles((prev) => [...prev, ...selectedFiles]);
      setPreviewUrls((prev) => [...prev, ...newUrls]);
    }
  };

  const handleRemoveImage = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    onSubmit(comment, files);
    resetAll();
  };

  const resetAll = () => {
    setComment("");
    setFiles([]);
    setPreviewUrls([]);
    setCameraMode(false);
    stopCamera();
    setSelectedQuestion()
  };

  if (!open) return null;

  const isSubmitDisabled = !comment.trim() && files.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-xl shadow-lg p-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Add Comment & Images
          </h2>
          <button
            onClick={() => {
              resetAll();
              onClose();
            }}
            className="text-gray-500 hover:text-gray-800 dark:hover:text-white"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm text-start font-medium text-gray-700 dark:text-gray-300 mb-1">
            Comment
          </label>
          <textarea
            className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-gray-800 dark:text-white"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter your comment..."
          />
        </div>

        {/* Upload or Capture */}
        <div className="mt-4">
          {!cameraMode && (
            <div className="flex flex-wrap gap-3">
              {/* Upload from device */}
              <label className="flex items-center gap-2 cursor-pointer bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-3 py-2 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                <UploadFileIcon fontSize="small" />
                <span>Upload Images</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>

              {/* Capture from camera */}
              <button
                onClick={() => setCameraMode(true)}
                className="flex items-center gap-2 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 transition"
              >
                <CameraAltIcon fontSize="small" />
                <span>Open Camera</span>
              </button>
            </div>
          )}

          {/* Camera View */}
          {cameraMode && (
            <div className="mt-4 flex flex-col items-center">
              <video
                ref={videoRef}
                autoPlay
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700"
              />
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-3 mt-3">
                <button
                  onClick={captureImage}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                >
                  <CameraIcon fontSize="small" />
                  Take Photo
                </button>
                <button
                  onClick={() => {
                    stopCamera();
                    setCameraMode(false);
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Preview Grid */}
          {previewUrls.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3">
              {previewUrls.map((url, index) => (
                <div key={index} className="relative">
                  <img
                    src={url}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded-md border border-gray-300 dark:border-gray-700"
                  />
                  <button
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 border-t pt-3">
          <button
            onClick={() => {
              resetAll();
              onClose();
            }}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled || loader}
            className={`px-4 py-2 rounded-md text-white font-medium ${
              isSubmitDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-black"
            }`}
          >
            {loader ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SafetyImageCommentModal;
